import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChild,
} from '@angular/core';
import { ShipcalendarComponent } from './shipcalendar.component';
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import {
  getCalendarWeek,
  utcCalendarDay,
} from '../../core/booking-from.utils';
import { BookingDataService } from '../../services/booking-data/booking-data.service';
import { DashboardFilterService } from '../../services/filter/dashboard-filter.service';
import { FilterState, Scope } from '../../core/models';
import { Subscription } from 'rxjs';
import { LoadingBarrierService } from '../../services/resources/loading-barrier.service';
import AirDatepicker from 'air-datepicker';
import en from 'air-datepicker/locale/de';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-shipcalendar-headerbar',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="w-full flex flex-col sm:flex-row sm:justify-between items-center gap-3 py-4"
    >
      <div class="flex items-center gap-3 w-full justify-between">
        <button
          (click)="onPrevWeek()"
          [disabled]="isSwitching()"
          type="button"
          class="rounded-md bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>
        </button>
        <div class="font-semibold text-base sm:text-2xl italic text-center">
          {{ title }}
        </div>
        <button
          (click)="onNextWeek()"
          [disabled]="isSwitching()"
          type="button"
          class="rounded-md bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m8.25 4.5 7.5 7.5-7.5 7.5"
            />
          </svg>
        </button>
      </div>
      <div class="flex items-center gap-3">
        <div class="relative z-20 airdp">
          <button
            type="button"
            (click)="triggerDatepicker()"
            class="rounded-md bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
              />
            </svg>
          </button>

          <input
            #datepickerInput
            type="text"
            class="absolute top-1/3 left-1/3 w-[1px] h-[1px] opacity-0 pointer-events-none"
            autocomplete="off"
          />
        </div>
        <button
          (click)="onToday()"
          type="button"
          class="rounded-md bg-columbus-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-columbus-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
        >
          Today
        </button>
      </div>
    </div>

    <div
      @opacity
      (click)="triggerDatepicker()"
      *ngIf="picker?.visible"
      class="fixed inset-0 z-10 bg-gray-500 bg-opacity-75 transition-opacity"
      aria-hidden="true"
    ></div>
  `,
  styles: ``,
  animations: [
    trigger('opacity', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('200ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class ShipcalendarHeaderbarComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  @Input({ required: true }) shipCalendars!: QueryList<ShipcalendarComponent>;
  @Output() headerDate = new EventEmitter<Date>();

  @ViewChild('datepickerInput', { static: true })
  datepickerInput!: ElementRef<HTMLInputElement>;

  protected picker?: AirDatepicker;

  title = '';

  bookingDataService = inject(BookingDataService);
  filterService = inject(DashboardFilterService);

  filterStateSubscription$: Subscription | undefined = undefined;
  barrier = inject(LoadingBarrierService);
  scope: Scope = 'bootstrap';

  readonly bootstrapReady = this.barrier.isScopeReady(this.scope);
  readonly isSwitching = signal(false);

  private readonly unlockOnReady = effect(() => {
    if (this.bootstrapReady() && this.isSwitching()) {
      this.isSwitching.set(false);
    }
  });

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.filterStateSubscription$ = this.filterService.filters$
        .pipe()
        .subscribe((fs: FilterState) => {
          let savedDate = new Date();
          if (fs && fs.savedDate) {
            savedDate = parseISO(fs.savedDate);
          }
          this.shipCalendars.forEach((shipCalendar) => {
            shipCalendar.getCalendarApi()?.gotoDate(savedDate);
          });
        });
      this.updateTitle();
    }, 0);
  }

  ngOnDestroy(): void {
    this.filterStateSubscription$?.unsubscribe();
  }

  updateTitle() {
    if (this.shipCalendars && this.shipCalendars.length > 0) {
      const shipCalendarApi = this.shipCalendars.first.getCalendarApi();
      if (shipCalendarApi) {
        const today = shipCalendarApi.getDate();
        this.filterService.updateCalendarWeek(today.toISOString());
        // The calendar runs in UTC, so the title uses the UTC calendar day as well.
        const day = utcCalendarDay(today);
        const startDate = startOfWeek(day, { weekStartsOn: 1 });

        const endDate = endOfWeek(day, { weekStartsOn: 1 });
        // Same week as the Firestore path (UTC-based), not the browser-local ISO week.
        const numWeek = Number(getCalendarWeek(today).slice(5));
        this.title =
          format(startDate, 'dd.MM.yyyy') +
          ' - ' +
          'KW ' +
          numWeek +
          ' ' +
          ' - ' +
          format(endDate, 'dd.MM.yyyy');
      }
    }
  }

  onNextWeek() {
    if (this.isSwitching()) return;
    this.isSwitching.set(true);
    this.barrier.resetScope(this.scope);
    this.shipCalendars.forEach((shipCalendar) => {
      const shipCalendarApi = shipCalendar.getCalendarApi();
      if (shipCalendarApi) {
        shipCalendarApi.next();
      }
    });
    this.updateTitle();
  }

  onPrevWeek() {
    if (this.isSwitching()) return;
    this.isSwitching.set(true);
    this.barrier.resetScope(this.scope);
    this.shipCalendars.forEach((shipCalendar) => {
      const shipCalendarApi = shipCalendar.getCalendarApi();
      if (shipCalendarApi) {
        shipCalendarApi.prev();
      }
    });

    this.updateTitle();
  }

  onToday() {
    if (this.isSwitching()) return;
    this.isSwitching.set(true);
    this.barrier.resetScope(this.scope);

    this.shipCalendars.forEach((shipCalendar) => {
      const shipCalendarApi = shipCalendar.getCalendarApi();
      if (shipCalendarApi) {
        shipCalendarApi.today();
      }
    });
    this.updateTitle();
  }

  goToDatePicker(date: Date) {
    if (this.isSwitching()) return;
    this.isSwitching.set(true);
    this.barrier.resetScope(this.scope);
    // The picker hands over local midnight; the calendars and the week key work on the UTC day.
    const utcDay = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    this.filterService.updateCalendarWeek(utcDay.toISOString());
    this.shipCalendars.forEach((shipCalendar) => {
      const shipCalendarApi = shipCalendar.getCalendarApi();
      if (shipCalendarApi) {
        shipCalendarApi.gotoDate(utcDay);
      }
    });
    this.updateTitle();
  }

  triggerDatepicker(): void {
    if (!this.picker) {
      this.picker = new AirDatepicker(this.datepickerInput.nativeElement, {
        locale: en,
        autoClose: true,
        multipleDates: false,
        dateFormat: 'dd.MM.yyyy',
        position: 'bottom right',
        onSelect: ({ date }) => {
          this.goToDatePicker(date as Date);
        },
      });
    }

    if (this.picker.visible) {
      this.picker.hide();
    } else {
      this.picker.show();
    }
  }
}
